class TabsBlock extends HTMLElement {
  constructor() {
    super();
    this.tablist = this.querySelector('.tabs-block__list');
    this.panelsContainer = this.querySelector('.tabs-block__panels');
    this.panels = Array.from(this.querySelectorAll('.tabs-block__panel'));
    if (!this.tablist || !this.panels.length) return;

    this.buttons = [];
    this.resizeObserver = null;
    this.init();
  }

  getActivePanelHeight() {
    const active = this.panels.find((p) => p.classList.contains('is-active'));
    return active ? active.offsetHeight : 0;
  }

  setPanelsHeight(height) {
    if (!this.panelsContainer) return;
    this.panelsContainer.style.height = height ? `${height}px` : '';
  }

  observeActivePanelHeight() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.setPanelsHeight(this.getActivePanelHeight());
    });
    const active = this.panels.find((p) => p.classList.contains('is-active'));
    if (active) this.resizeObserver.observe(active);
  }

  init() {
    this.panels.forEach((panel, index) => {
      const title = panel.dataset.tabTitle || '';
      const panelId = panel.id;
      const tabId = panelId.replace(/^TabPanel-/, '');
      const buttonId = `Tab-${tabId}`;

      const button = document.createElement('button');
      button.type = 'button';
      button.id = buttonId;
      button.className = 'tabs-block__tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panelId);
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('tabindex', index === 0 ? 0 : -1);
      button.textContent = title;

      button.addEventListener('click', () => this.activate(index));
      this.tablist.appendChild(button);
      this.buttons.push(button);
    });

    this.panels.forEach((panel, index) => {
      panel.setAttribute('aria-hidden', index !== 0 ? 'true' : 'false');
      if (index === 0) panel.classList.add('is-active');
    });

    if (this.buttons[0]) this.buttons[0].classList.add('is-active');

    this.classList.add('js-initialized');
    this.setPanelsHeight(this.getActivePanelHeight());
    this.observeActivePanelHeight();
    this.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  activate(index) {
    if (index < 0 || index >= this.panels.length) return;

    const currentHeight = this.panelsContainer.offsetHeight;
    const targetPanel = this.panels[index];

    this.buttons.forEach((button, i) => {
      const isSelected = i === index;
      button.setAttribute('aria-selected', isSelected);
      button.setAttribute('tabindex', isSelected ? 0 : -1);
      button.classList.toggle('is-active', isSelected);
    });

    this.panels.forEach((panel, i) => {
      const isSelected = i === index;
      panel.classList.toggle('is-active', isSelected);
      panel.setAttribute('aria-hidden', !isSelected);
    });

    this.setPanelsHeight(currentHeight);
    requestAnimationFrame(() => {
      this.setPanelsHeight(targetPanel.offsetHeight);
      this.observeActivePanelHeight();
    });
  }

  onKeyDown(event) {
    const currentIndex = this.buttons.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = currentIndex < this.buttons.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : this.buttons.length - 1;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = this.buttons.length - 1;
        break;
      default:
        return;
    }

    this.activate(nextIndex);
    this.buttons[nextIndex].focus();
  }
}

customElements.define('tabs-block', TabsBlock);
